import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import { SetupStoreId } from '@/store/plugins';
import { useRouterPush } from '@/hooks/common/router';
import { localStg } from '@/utils/storage';

export const useAuthStore = defineStore(SetupStoreId.Auth, () => {
  const route = useRoute();
  const { toLogin, redirectFromLogin } = useRouterPush(false);
  const token = ref(getToken());

  const userInfo = reactive({
    userId: '',
    userName: '',
    roles: [] as string[],
    buttons: [] as string[]
  });

  /** 是否静态路由中的超级角色 */
  const isStaticSuper = computed(() => {
    const { VITE_AUTH_ROUTE_MODE, VITE_STATIC_SUPER_ROLE } = import.meta.env;

    return VITE_AUTH_ROUTE_MODE === 'static' && userInfo.roles.includes(VITE_STATIC_SUPER_ROLE);
  });

  /** 是否已登录 */
  const isLogin = computed(() => Boolean(token.value));

  /** 重置auth store */
  async function resetStore() {
    clearAuthStorage();
    token.value = '';
    Object.assign(userInfo, {
      userId: '',
      userName: '',
      roles: [],
      buttons: []
    });
  }

  /** 获取token */
  function getToken() {
    return localStg.get('token') || '';
  }

  /** 清除认证相关的本地存储 */
  function clearAuthStorage() {
    localStg.remove('token');
    localStg.remove('refreshToken');
  }

  /**
   * 登录
   *
   * @param userName 用户名
   * @param password 密码
   * @param [redirect=true] 登录后是否重定向，默认为 `true`
   */
  async function login(userName: string, password: string, redirect = true) {
    // 这里模拟登录，实际项目中应该调用登录API
    const loginToken = {
      token: 'mock-token',
      refreshToken: 'mock-refresh-token'
    };
    
    await loginByToken(loginToken);
    
    if (redirect) {
      await redirectFromLogin(redirect);
    }
  }

  async function loginByToken(loginToken: { token: string; refreshToken: string }) {
    // 1. 存储在本地存储中，后续请求需要在headers中使用
    localStg.set('token', loginToken.token);
    localStg.set('refreshToken', loginToken.refreshToken);

    // 2. 获取用户信息
    const pass = await getUserInfo();

    if (pass) {
      token.value = loginToken.token;
      return true;
    }

    return false;
  }

  async function getUserInfo() {
    // 模拟获取用户信息
    // 实际项目中应该调用获取用户信息API
    const info = {
      userId: '1',
      userName: 'Admin',
      roles: ['admin'],
      buttons: ['add', 'edit', 'delete']
    };

    // 更新store
    Object.assign(userInfo, info);

    return true;
  }

  async function initUserInfo() {
    const hasToken = getToken();

    if (hasToken) {
      const pass = await getUserInfo();

      if (!pass) {
        resetStore();
      }
    }
  }

  return {
    token,
    userInfo,
    isStaticSuper,
    isLogin,
    resetStore,
    login,
    initUserInfo,
    getToken,
    clearAuthStorage
  };
}); 